import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, X, FileText, FilePlus2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import DashboardCard from '@/components/ui/DashboardCard';
import GlassPill from '@/components/ui/GlassPill';
import DashboardButton from '@/components/ui/DashboardButton';
import SidebarLayout from '@/components/layout/SidebarLayout';
import { useProjectNavigation } from '@/hooks/goal-planner/use-optimized-navigation';
import { useDebouncedValue } from '@/lib/utils';
import PageGrid from '@/components/content-generator/pages/PageGrid';
import UniversalMultiselectSidebar from '@/components/content-generator/editor/sidebar/UniversalMultiselectSidebar';
import { useBulkPageImport } from '@/hooks/content-generator/useBulkPageImport';

interface WorkbookPagesTabProps {
  pageId: string;
  onCreatePage: () => void;
  onEditPage: (pageId: string) => void;
}

export const WorkbookPagesTab: React.FC<WorkbookPagesTabProps> = ({
  pageId,
  onCreatePage,
  onEditPage,
}) => {
  // Search functionality (same pattern as PrivateImageLibrary)
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery, 500);

  // Bulk import functionality
  const bulkImportMutation = useBulkPageImport(pageId);

  const handleBulkImport = async (selectedPageIds: string[]) => {
    await bulkImportMutation.mutateAsync(selectedPageIds);
  };

  // Get project navigation data (includes pages)
  const { data: navigationData, isLoading } = useProjectNavigation(pageId);
  const pages = navigationData?.pages || [];

  // Transform pages to include workbook_id for the project context
  // Since these are project pages, they're associated with this project
  const pagesWithWorkbookId = pages.map(page => ({
    ...page,
    workbook_id: pageId // All pages in project view are associated with this project
  }));

  // Filter pages based on search query
  const filteredPages = pagesWithWorkbookId.filter(page => {
    if (!debouncedSearch) return true;
    
    const searchLower = debouncedSearch.toLowerCase();
    return (
      page.title?.toLowerCase().includes(searchLower) ||
      page.type?.toLowerCase().includes(searchLower)
    );
  });

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  return (
    <SidebarLayout sidebar={
      <UniversalMultiselectSidebar 
        pageId={pageId} 
        contentType="pages"
        onBulkImport={handleBulkImport}
      />
    }>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.1 }}
        className="space-y-6"
      >
        {/* Main Pages Section - Same structure as PrivateImageLibrary */}
        <DashboardCard className="p-6">
          {/* Header with Document Count and Create Document Button */}
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <div className="flex items-center gap-2">
              <FileText className="text-purple-500 dark:text-purple-400" size={18} />
              <h3 className="font-medium text-base">Shared Documents</h3>
              {filteredPages.length > 0 && (
                <GlassPill 
                  type={filteredPages.length.toString()} 
                  variant="purple" 
                  className="ml-2 text-xs h-6 w-6 flex items-center justify-center p-0"
                />
              )}
            </div>
            
            {/* Create Document Button - Desktop only */}
            <div className="hidden sm:block">
              <DashboardButton
                onClick={onCreatePage}
                icon={FilePlus2}
                size="sm"
                className="h-8 px-3 py-1.5 text-sm"
              >
                Create Document
              </DashboardButton>
            </div>
          </div>
          
          {/* Mobile Create Document Button - Full width below header */}
          <div className="sm:hidden mb-4">
            <DashboardButton
              onClick={onCreatePage}
              icon={FilePlus2}
              size="sm"
              className="h-8 px-3 py-1.5 text-sm w-full"
            >
              Create Document
            </DashboardButton>
          </div>

          {/* Search Results Info */}
          {searchQuery && (
            <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
              {filteredPages.length === 0 ? (
                <>No documents found for "<strong>{searchQuery}</strong>"</>
              ) : filteredPages.length === 1 ? (
                <>Found 1 document for "<strong>{searchQuery}</strong>"</>
              ) : (
                <>Found {filteredPages.length} documents for "<strong>{searchQuery}</strong>"</>
              )}
            </div>
          )}

          {/* Pages Grid - Show Add to Project buttons since pages can be moved between projects */}
          <PageGrid 
            pages={filteredPages}
            onPageEdit={onEditPage}
            onCreatePage={onCreatePage}
            isLoading={isLoading}
            hideProjectButton={false}
          />
        </DashboardCard>
      </motion.div>
    </SidebarLayout>
  );
}; 